import { Component, ElementRef, HostListener, Input, forwardRef, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { BusinessClock } from '../../core/time/business-clock.service';
import { JstDate } from '../../core/time/business-time';

export function validOptionalBusinessDate(value: unknown): boolean {
  if (value == null || value === '') return true;
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value + 'T00:00:00.000Z');
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

@Component({
  selector: 'app-business-date-input',
  templateUrl: './business-date-input.component.html',
  styleUrl: './business-date-input.component.css',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => BusinessDateInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => BusinessDateInputComponent), multi: true },
  ],
})
export class BusinessDateInputComponent implements ControlValueAccessor, Validator {
  private readonly clock = inject(BusinessClock);
  private readonly element: ElementRef<HTMLElement> = inject(ElementRef);
  @Input() inputName = 'counseled_at';
  value = '';
  disabled = false;
  expanded = false;
  year = 0;
  month = 0;
  focusedDay = '';
  readonly weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  private changed: (value: string) => void = () => {};
  private touched: () => void = () => {};

  get invalid(): boolean { return !validOptionalBusinessDate(this.value); }
  writeValue(value: string | null | undefined): void { this.value = value ?? ''; }
  registerOnChange(fn: (value: string) => void): void { this.changed = fn; }
  registerOnTouched(fn: () => void): void { this.touched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; if (disabled) this.expanded = false; }
  validate(_control: AbstractControl): ValidationErrors | null { return this.invalid ? { businessDate: true } : null; }

  input(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.changed(this.value);
  }

  async openCalendar(focus = false): Promise<void> {
    if (this.disabled || !await this.clock.synchronize()) return;
    if (!this.element.nativeElement.contains(document.activeElement)) return;
    const date = this.value && !this.invalid ? new JstDate(this.value) : new JstDate();
    this.year = date.getFullYear();
    this.month = date.getMonth();
    this.focusedDay = this.format(this.year, this.month, date.getDate());
    this.expanded = true;
    if (focus) this.focusCell();
  }

  get days(): Array<{ value: string; day: number } | null> {
    const first = new JstDate(this.year, this.month, 1);
    const last = new JstDate(this.year, this.month + 1, 0).getDate();
    return [
      ...Array.from({ length: first.getDay() }, () => null),
      ...Array.from({ length: last }, (_, n) => ({ value: this.format(this.year, this.month, n + 1), day: n + 1 })),
    ];
  }

  moveMonth(offset: number): void {
    const date = new JstDate(this.year, this.month + offset, 1);
    if (date.getFullYear() < 100 || date.getFullYear() > 9999) return;
    this.year = date.getFullYear(); this.month = date.getMonth();
    this.focusedDay = this.format(this.year, this.month, 1);
  }

  choose(value: string): void {
    this.value = value; this.changed(value); this.touched(); this.expanded = false;
    // Focusing must not reopen a just-completed selection.
    this.element.nativeElement.querySelector('input')?.focus({ preventScroll: true });
    this.expanded = false;
  }

  clear(): void { this.choose(''); }

  @HostListener('focusout', ['$event'])
  blur(event: FocusEvent): void {
    this.touched();
    if (!this.element.nativeElement.contains(event.relatedTarget as Node | null)) this.expanded = false;
  }

  @HostListener('document:click', ['$event'])
  outside(event: MouseEvent): void {
    if (!this.element.nativeElement.contains(event.target as Node)) this.expanded = false;
  }

  key(event: KeyboardEvent): void {
    if (event.key === 'Escape') { event.preventDefault(); this.expanded = false; return; }
    if (event.target instanceof HTMLInputElement) {
      if (event.key === 'ArrowDown') { event.preventDefault(); void this.openCalendar(true); }
      return;
    }
    const offset = ({ ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 } as Record<string, number>)[event.key];
    if (offset === undefined || !this.focusedDay) return;
    event.preventDefault();
    const date = new JstDate(this.focusedDay);
    date.setDate(date.getDate() + offset);
    this.year = date.getFullYear(); this.month = date.getMonth();
    this.focusedDay = this.format(this.year, this.month, date.getDate());
    this.focusCell();
  }

  private focusCell(): void {
    setTimeout(() => this.element.nativeElement.querySelector<HTMLButtonElement>('[data-date="' + this.focusedDay + '"]')?.focus(), 0);
  }
  private format(year: number, month: number, day: number): string {
    return String(year).padStart(4, '0') + '-' + String(month + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
  }
}
